import { formatCurrency, formatQuantityCell, priceUnitLabel } from '~/utils';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import { unitQuantityOfPieces } from '~/components/ui/bundleQuantity';
import type { ItemRow } from '~/pages/rfq-view/itemGroups';
import type { Currency, UnitCode } from '~/graphql/graphql';

type PlatformProductLengthKeyed = { platformProductsLength: { id: string } };

type OfferedQuantity = {
  quantityInPieces: number;
  piecesPerBundle?: number | null;
};

type BundledRow = { piecesPerBundle: number };

export function offeredBundleQuantity(
  item: OfferedQuantity,
  row: BundledRow
): number {
  return quantityInBundles(
    item.quantityInPieces,
    item.piecesPerBundle ?? row.piecesPerBundle
  );
}

export function showsSplitAwardGrid(
  requestForQuote: { useTallyPrice: boolean },
  quotes: readonly unknown[]
): boolean {
  return quotes.length > 0 && !requestForQuote.useTallyPrice;
}

export function requestedQuantityInStandardUnits(
  row: ItemRow,
  standardUnitsCode: UnitCode
): string | null {
  if (row.requestedQuantityInPieces == null || row.bundleDimensions == null) {
    return null;
  }
  const quantity = unitQuantityOfPieces(
    row.bundleDimensions,
    row.requestedQuantityInPieces,
    standardUnitsCode
  );
  return quantity == null
    ? null
    : formatQuantityCell(quantity, standardUnitsCode);
}

export function moneyOrDash(
  money: { amount: number; currency: Currency } | null | undefined
): string {
  return money ? formatCurrency(money.amount, money.currency) : '—';
}

export function itemsByPlatformProductLengthId<
  T extends PlatformProductLengthKeyed,
>(items: T[]): Map<string, T> {
  return new Map(items.map((item) => [item.platformProductsLength.id, item]));
}

export function priceLabels(
  standardUnitsCode: string,
  t: (key: string) => string
): { pickupPriceLabel: string; deliveryPriceLabel: string } {
  const priceUnitSuffix = `(/${priceUnitLabel(standardUnitsCode)})`;
  return {
    pickupPriceLabel:
      `${t('rfq_view.pickup_price_label')} ${priceUnitSuffix}`.trim(),
    deliveryPriceLabel:
      `${t('rfq_view.delivery_price_label')} ${priceUnitSuffix}`.trim(),
  };
}
